/**
 * Landing Map — Interactive 1989/Today Map Overlay
 * Target: assets/js/story/landing-map.js
 *
 * Standalone module (no StoryCore/StoryAPI dependency).
 * Configured via window.landingConfig (set in Ghost Code Injection).
 *
 * Features:
 *   - Layer blending (1989 → Heute) based on visited marker count
 *   - Pulsing border APNG that fades with progress
 *   - SVG markers with visited state (localStorage)
 *   - Viewport zoom/pan via CSS transform (configurable)
 *   - ResizeObserver for marker repositioning
 *   - img.decode() for jank-free first paint
 *   - Lazy loading: mapToday loaded after initial paint
 *   - Progressive loading: low-res 1989 first, high-res swap in background
 *   - ?debug=landing for console output
 *
 * Pattern: IIFE for Ghost compatibility (concatenated build)
 */

(function(window) {
  'use strict';

  var DEBUG = window.location.search.indexOf('debug=landing') !== -1;

  function log() {
    if (DEBUG) {
      var args = ['[Landing]'].concat(Array.prototype.slice.call(arguments));
      console.log.apply(console, args);
    }
  }

  function clamp(val, min, max) {
    return Math.max(min, Math.min(max, val));
  }

  // =========================================
  // CONFIG VALIDATION
  // =========================================

  function getConfig() {
    var cfg = window.landingConfig;
    if (!cfg) {
      console.warn('[Landing] No window.landingConfig found. Landing map inactive.');
      return null;
    }

    if (!cfg.map1989 || !cfg.mapToday) {
      console.warn('[Landing] landingConfig incomplete: map1989 and mapToday are required.');
      return null;
    }

    if (!cfg.markers || !cfg.markers.length) {
      console.warn('[Landing] landingConfig.markers is empty or missing.');
      return null;
    }

    // Defaults
    cfg.version = cfg.version || 1;
    cfg.viewport = cfg.viewport || {};
    cfg.viewport.scale = cfg.viewport.scale || 1;
    cfg.viewport.originX = cfg.viewport.originX || 50;  // % from left
    cfg.viewport.originY = cfg.viewport.originY || 50;  // % from top
    cfg.markerIcon = cfg.markerIcon || '';
    cfg.markerIconVisited = cfg.markerIconVisited || cfg.markerIcon;
    cfg.viewport.maxZoom = cfg.viewport.maxZoom || 5;

    return cfg;
  }

  // =========================================
  // LOCALSTORAGE (versioned key)
  // =========================================

  function storageKey(cfg) {
    return 'grenzwandler:landing:v' + cfg.version + ':visited';
  }

  function loadVisited(cfg) {
    try {
      var raw = localStorage.getItem(storageKey(cfg));
      if (raw) {
        var arr = JSON.parse(raw);
        if (Array.isArray(arr)) return arr;
      }
    } catch (e) {
      log('Could not load visited state:', e.message);
    }
    return [];
  }

  function saveVisited(cfg, visited) {
    try {
      localStorage.setItem(storageKey(cfg), JSON.stringify(visited));
    } catch (e) {
      log('Could not save visited state:', e.message);
    }
  }

  // =========================================
  // IMAGE DECODE HELPER
  // =========================================

  function decodeImage(img) {
    return new Promise(function(resolve, reject) {
      if (img.decode) {
        // Modern browsers: decode() resolves when image is ready to paint
        img.decode().then(resolve).catch(reject);
      } else {
        // Fallback: wait for load event
        if (img.complete && img.naturalWidth > 0) {
          resolve();
        } else {
          img.onload = resolve;
          img.onerror = reject;
        }
      }
    });
  }

  // =========================================
  // PERFORMANCE TIMING (active with ?debug=landing)
  // =========================================

  var perf = { images: [], t0: 0 };

  /**
   * Load image with separate fetch/decode timing.
   * Works in all modes; only records perf data when DEBUG is on.
   */
  function loadWithTiming(img, url, label) {
    var fetchStart = performance.now();

    img.src = url;

    return new Promise(function(resolve, reject) {
      function afterFetch() {
        var fetchMs = Math.round(performance.now() - fetchStart);

        if (img.decode) {
          var decodeStart = performance.now();
          img.decode().then(function() {
            var decodeMs = Math.round(performance.now() - decodeStart);
            if (DEBUG) {
              perf.images.push({ label: label, url: url, fetchMs: fetchMs, decodeMs: decodeMs });
              log(label + ': fetch ' + fetchMs + 'ms + decode ' + decodeMs + 'ms');
            }
            resolve();
          }).catch(reject);
        } else {
          if (DEBUG) {
            perf.images.push({ label: label, url: url, fetchMs: fetchMs, decodeMs: 0 });
            log(label + ': fetch ' + fetchMs + 'ms');
          }
          resolve();
        }
      }

      if (img.complete && img.naturalWidth > 0) {
        afterFetch();
      } else {
        img.onload = afterFetch;
        img.onerror = reject;
      }
    });
  }

  /**
   * Print performance summary table (Resource Timing API for sizes).
   */
  function reportPerf(phase) {
    if (!DEBUG || !perf.images.length) return;

    var hasRT = typeof performance.getEntriesByName === 'function';

    var rows = perf.images.map(function(t) {
      var entry = null;
      if (hasRT) {
        var entries = performance.getEntriesByName(t.url, 'resource');
        if (entries.length) entry = entries[entries.length - 1];
      }
      return {
        Layer: t.label,
        'Fetch ms': t.fetchMs,
        'Decode ms': t.decodeMs,
        'Total ms': t.fetchMs + t.decodeMs,
        'Transfer KB': entry && entry.transferSize ? Math.round(entry.transferSize / 1024) : '(cached/opaque)',
        'Size KB': entry && entry.decodedBodySize ? Math.round(entry.decodedBodySize / 1024) : '—'
      };
    });

    log('=== PERF ' + (phase || '') + ' (since init: ' + Math.round(performance.now() - perf.t0) + 'ms) ===');
    console.table(rows);
  }

  // =========================================
  // MARKER RENDERING
  // =========================================

  function renderMarkers(cfg, container, visited) {
    var frag = document.createDocumentFragment();

    cfg.markers.forEach(function(marker) {
      var isVisited = visited.indexOf(marker.id) !== -1;
      var iconUrl = isVisited
        ? (cfg.markerIconVisited || cfg.markerIcon)
        : cfg.markerIcon;

      var btn = document.createElement('button');
      btn.className = 'landing-map__marker' + (isVisited ? ' landing-map__marker--visited' : '');
      btn.setAttribute('data-marker-id', marker.id);
      btn.setAttribute('data-marker-url', marker.url);
      btn.setAttribute('aria-label', marker.title || marker.label || marker.id);

      // Position as CSS percentage of marker layer (set once, never recalculated)
      btn.style.left = marker.x + '%';
      btn.style.top = marker.y + '%';

      if (iconUrl) {
        var icon = document.createElement('img');
        icon.className = 'landing-map__marker-icon';
        icon.src = iconUrl;
        icon.alt = '';
        icon.draggable = false;
        btn.appendChild(icon);
      }

      // Custom tooltip (title + label)
      var hasTitle = marker.title && marker.title.length > 0;
      var hasLabel = marker.label && marker.label.length > 0;
      if (hasTitle || hasLabel) {
        var tooltip = document.createElement('span');
        tooltip.className = 'landing-map__marker-tooltip';
        if (hasTitle) {
          var titleSpan = document.createElement('span');
          titleSpan.className = 'landing-map__marker-tooltip__title';
          titleSpan.textContent = marker.title;
          tooltip.appendChild(titleSpan);
        }

        if (hasLabel) {
          var labelSpan = document.createElement('span');
          labelSpan.className = 'landing-map__marker-tooltip__label';
          labelSpan.textContent = marker.label;
          tooltip.appendChild(labelSpan);
        }
        btn.appendChild(tooltip);
      }

      frag.appendChild(btn);
    });

    container.innerHTML = '';
    container.appendChild(frag);
  }

  // =========================================
  // CANVAS SIZING (replaces object-fit:contain)
  // =========================================

  /**
   * Resize the canvas wrapper to contain the image at correct aspect ratio,
   * centered within the viewport. All children (images + markers) inherit
   * this size — single source of truth, no drift possible.
   */
  function resizeCanvas(viewportEl, canvasEl, imgRatio) {
    if (!imgRatio) return null;

    var vw = viewportEl.clientWidth;
    var vh = viewportEl.clientHeight;
    var vRatio = vw / vh;
    var w, h;

    if (imgRatio > vRatio) {
      // Image wider than viewport → letterbox top/bottom
      w = vw;
      h = vw / imgRatio;
    } else {
      // Image taller than viewport → letterbox left/right
      h = vh;
      w = vh * imgRatio;
    }

    var left = (vw - w) / 2;
    var top = (vh - h) / 2;

    canvasEl.style.width = w + 'px';
    canvasEl.style.height = h + 'px';
    canvasEl.style.left = left + 'px';
    canvasEl.style.top = top + 'px';

    log('Canvas resized:', {
      viewport: vw + 'x' + vh,
      canvas: Math.round(w) + 'x' + Math.round(h),
      offset: Math.round(left) + ',' + Math.round(top)
    });

    return { left: left, top: top, width: w, height: h, vpW: vw, vpH: vh };
  }

  // =========================================
  // LAYER OPACITY (progress-based)
  // =========================================

  function updateLayers(cfg, visited, layerToday, layerBorder, todayReady) {
    var total = cfg.markers.length;
    var count = visited.length;
    var progress = total > 0 ? count / total : 0;

    // Today map: 0 → 1 with progress (only if loaded)
    if (todayReady) {
      layerToday.style.opacity = progress;
    }

    // Border APNG: 1 → 0 with progress (only if visible)
    if (layerBorder && layerBorder.style.visibility !== 'hidden') {
      layerBorder.style.opacity = 1 - progress;
    }

    log('Layers:', {
      visited: count + '/' + total,
      progress: Math.round(progress * 100) + '%',
      todayOpacity: todayReady ? progress.toFixed(2) : 'not loaded',
      borderOpacity: (1 - progress).toFixed(2)
    });
  }

  // =========================================
  // PROGRESS DISPLAY
  // =========================================

  function updateProgress(cfg, visited, progressEl) {
    if (!progressEl) return;
    progressEl.textContent = visited.length + '/' + cfg.markers.length;
  }

  // =========================================
  // MARKER ICON UPDATE
  // =========================================

  function updateMarkerIcon(btn, iconUrl) {
    if (!iconUrl) return;
    var img = btn.querySelector('.landing-map__marker-icon');
    if (img) img.src = iconUrl;
  }

  // =========================================
  // LAZY LOAD: mapToday
  // =========================================

  function lazyLoadToday(cfg, layerToday, visited, layerBorder, state) {
    if (state.todayLoading || state.todayReady) return;
    state.todayLoading = true;

    var url = cfg.mapTodayLow || cfg.mapToday;
    var label = cfg.mapTodayLow ? 'mapToday LOW' : 'mapToday';
    log(label + ' queued for lazy load');

    loadWithTiming(layerToday, url, label).then(function() {
      state.todayReady = true;
      log('mapToday loaded/decoded');

      // Apply opacity based on current visited state
      updateLayers(cfg, visited, layerToday, layerBorder, true);
      reportPerf('after mapToday');
    }).catch(function() {
      log('mapToday failed — continuing without today layer');
      state.todayReady = false;
    });
  }

  // =========================================
  // LAZY LOAD: border APNG
  // =========================================

  function lazyLoadBorder(cfg, layerBorder, visited, state) {
    if (!cfg.border || !layerBorder || state.borderLoading || state.borderReady) return;

    // Skip entirely if all markers visited (border would be opacity 0)
    var progress = cfg.markers.length > 0 ? visited.length / cfg.markers.length : 0;
    if (progress >= 1) {
      log('border APNG skipped — all markers visited');
      return;
    }

    // Skip on slow devices: if first paint took >3s, the browser is struggling
    var elapsed = performance.now() - perf.t0;
    if (elapsed > 3000) {
      log('border APNG skipped — slow device (' + Math.round(elapsed) + 'ms to first paint)');
      return;
    }

    state.borderLoading = true;
    log('border APNG queued for lazy load');

    // Keep visibility:hidden during load to prevent partial-render flash
    // (visibility allows CSS transition on opacity to work, display:none does not)
    loadWithTiming(layerBorder, cfg.border, 'border APNG').then(function() {
      state.borderReady = true;
      layerBorder.style.visibility = 'visible';
      layerBorder.style.opacity = 1 - progress;
      log('border APNG loaded — fading to opacity: ' + (1 - progress).toFixed(2));
      reportPerf('after border');
    }).catch(function() {
      log('border APNG failed — hidden');
    });
  }

  // =========================================
  // PROGRESSIVE LOAD: High-Res upgrades
  // =========================================

  function upgradeToHighRes(cfg, layer1989, state) {
    // Only upgrade if low-res was used AND high-res not yet loaded
    if (!cfg.map1989Low || state.map1989HiRes) return;

    log('map1989 HIGH queued');

    var hiImg = new Image();

    loadWithTiming(hiImg, cfg.map1989, 'map1989 HIGH').then(function() {
      layer1989.src = cfg.map1989;
      state.map1989HiRes = true;
      log('map1989 HIGH loaded — swapped');
    }).catch(function() {
      log('map1989 HIGH failed — staying with low-res');
    });
  }

  function upgradeTodayHighRes(cfg, layerToday, state) {
    if (!cfg.mapTodayLow || state.mapTodayHiRes) return;
    if (!state.todayReady) return;  // Wait until low-res is loaded first

    log('mapToday HIGH queued');

    var hiImg = new Image();

    loadWithTiming(hiImg, cfg.mapToday, 'mapToday HIGH').then(function() {
      layerToday.src = cfg.mapToday;
      state.mapTodayHiRes = true;
      log('mapToday HIGH loaded — swapped');
    }).catch(function() {
      log('mapToday HIGH failed — staying with low-res');
    });
  }

  // =========================================
  // MAIN INIT
  // =========================================

  function init() {
    perf.t0 = performance.now();

    var cfg = getConfig();
    if (!cfg) {
      // Hide loader gracefully
      var loader = document.getElementById('landingLoader');
      if (loader) loader.style.display = 'none';
      return;
    }

    log('Config:', cfg);

    // DOM references
    var viewportEl = document.getElementById('landingViewport');
    var canvasEl = document.getElementById('landingCanvas');
    var markersEl = document.getElementById('landingMarkers');
    var loaderEl = document.getElementById('landingLoader');
    var progressEl = document.getElementById('progressText');
    var layer1989 = document.getElementById('layer1989');
    var layerToday = document.getElementById('layerToday');
    var layerBorder = document.getElementById('layerBorder');

    if (!viewportEl || !canvasEl || !markersEl || !layer1989 || !layerToday) {
      console.warn('[Landing] Required DOM elements not found.');
      return;
    }

    // Cached image aspect ratio — set once after first image load
    var imgRatio = 0;

    // Load persisted state
    var visited = loadVisited(cfg);
    log('Restored visited:', visited);

    // Shared state for lazy/progressive loading
    var state = {
      todayReady: false, todayLoading: false,
      borderReady: false, borderLoading: false,
      map1989HiRes: false,
      mapTodayHiRes: false
    };

    // =========================================
    // ZOOM / PAN STATE
    // =========================================

    var sectionEl = document.getElementById('landingMap');
    var zoomSteps = [0.7, 1, 1.5, 2, 3, 4];
    var minZoom = zoomSteps[0];
    var maxZoom = zoomSteps[zoomSteps.length - 1];
    var canvasDims = null;
    var wasDragging = false;

    var zoom = {
      scale: clamp(cfg.viewport.scale, minZoom, maxZoom),
      panX: 0,
      panY: 0
    };

    var animationCount = 0;

    function applyTransform(animate) {
      var s = zoom.scale;
      var tx = zoom.panX;
      var ty = zoom.panY;


      if (animate) {
        animationCount++;
        // Step 1: activate GPU layer
        viewportEl.classList.add('landing-map__viewport--animating');
        viewportEl.classList.remove('landing-map__viewport--gesturing');
        // Force browser to process will-change immediately
        void viewportEl.offsetHeight;
        // Step 2: double-rAF — give GPU two frames to build texture,
        // THEN change transform values — prevents white flash
        requestAnimationFrame(function() {
          requestAnimationFrame(function() {
            viewportEl.style.setProperty('--vp-scale', s);
            viewportEl.style.setProperty('--vp-tx', tx + 'px');
            viewportEl.style.setProperty('--vp-ty', ty + 'px');
          });
        });
      } else {
        // Instant move (drag, resize) — no transition, no GPU layer
        animationCount = 0;
        viewportEl.classList.add('landing-map__viewport--gesturing');
        viewportEl.classList.remove('landing-map__viewport--animating');
        viewportEl.style.setProperty('--vp-scale', s);
        viewportEl.style.setProperty('--vp-tx', tx + 'px');
        viewportEl.style.setProperty('--vp-ty', ty + 'px');
      }
    }

    function clampPan() {
      if (!canvasDims) return;
      var s = zoom.scale;
      var secW = canvasDims.vpW;
      var secH = canvasDims.vpH;
      var cL = canvasDims.left;
      var cT = canvasDims.top;
      var cW = canvasDims.width;
      var cH = canvasDims.height;

      // Horizontal: canvas must cover section width
      var txMax = -cL * s;
      var txMin = secW - (cL + cW) * s;
      if (txMin > txMax) {
        zoom.panX = (txMin + txMax) / 2;
      } else {
        zoom.panX = clamp(zoom.panX, txMin, txMax);
      }

      // Vertical: canvas must cover section height
      var tyMax = -cT * s;
      var tyMin = secH - (cT + cH) * s;
      if (tyMin > tyMax) {
        zoom.panY = (tyMin + tyMax) / 2;
      } else {
        zoom.panY = clamp(zoom.panY, tyMin, tyMax);
      }
    }

    function zoomAtPoint(newScale, clientX, clientY) {
      var rect = sectionEl.getBoundingClientRect();
      var vpX = clientX - rect.left;
      var vpY = clientY - rect.top;

      // World coordinate before zoom
      var worldX = (vpX - zoom.panX) / zoom.scale;
      var worldY = (vpY - zoom.panY) / zoom.scale;

      // Apply new scale
      zoom.scale = clamp(newScale, minZoom, maxZoom);

      // Recalculate pan to keep world point under cursor
      zoom.panX = vpX - worldX * zoom.scale;
      zoom.panY = vpY - worldY * zoom.scale;

      clampPan();
    }

    function zoomCentered(newScale) {
      // Use viewport center (window), not section center — avoids jump
      // when section is partially scrolled out of view
      var rect = sectionEl.getBoundingClientRect();
      var cx = rect.left + rect.width / 2;
      // Clamp vertical center to visible portion of section
      var visTop = Math.max(rect.top, 0);
      var visBot = Math.min(rect.bottom, window.innerHeight);
      var cy = (visTop + visBot) / 2;
      zoomAtPoint(newScale, cx, cy);
    }

    function computeInitialFraming() {
      var vw = viewportEl.clientWidth;
      var vh = viewportEl.clientHeight;
      var oxPx = vw * (cfg.viewport.originX / 100);
      var oyPx = vh * (cfg.viewport.originY / 100);
      zoom.scale = clamp(cfg.viewport.scale, minZoom, maxZoom);
      zoom.panX = oxPx * (1 - zoom.scale);
      zoom.panY = oyPx * (1 - zoom.scale);
      clampPan();
    }

    // === Mobile optimization: small images only, no border APNG ===
    var isMobile = window.innerWidth < 768;
    if (isMobile) {
      log('Mobile detected — using low-res images, skipping border APNG');
      cfg.map1989 = cfg.map1989Low || cfg.map1989;
      cfg.map1989Low = null;   // No high-res upgrade needed
      cfg.mapToday = cfg.mapTodayLow || cfg.mapToday;
      cfg.mapTodayLow = null;  // No high-res upgrade needed
      cfg.border = null;       // Skip border APNG download
    }

    // === Start at overview — animated zoom to config after load ===
    zoom.scale = cfg.viewport.introScale || 1;
    zoom.panX = 0;
    zoom.panY = 0;
    applyTransform(false);

    // === Load initial images (map1989 + border only) ===
    var imagesToLoad = [];
    var map1989Url = cfg.map1989Low || cfg.map1989;
    var map1989Label = cfg.map1989Low ? 'map1989 LOW' : 'map1989';

    imagesToLoad.push(loadWithTiming(layer1989, map1989Url, map1989Label));

    // mapToday: eager load for returning visitors, lazy for first-timers
    var mapTodayUrl = cfg.mapTodayLow || cfg.mapToday;
    var mapTodayLabel = cfg.mapTodayLow ? 'mapToday LOW (eager)' : 'mapToday (eager)';
    if (visited.length > 0) {
      state.todayLoading = true;
      imagesToLoad.push(
        loadWithTiming(layerToday, mapTodayUrl, mapTodayLabel).then(function() {
          state.todayReady = true;
        }).catch(function() {
          log('mapToday eager load failed — will retry lazily');
          state.todayLoading = false;
        })
      );
    }

    // Border APNG: deferred to after first paint (saves ~900ms decode)
    // CSS starts with opacity:0 + visibility:hidden; JS sets visibility:visible after decode

    // Preload marker icons
    if (cfg.markerIcon) {
      var pre1 = new Image();
      pre1.src = cfg.markerIcon;
    }
    if (cfg.markerIconVisited && cfg.markerIconVisited !== cfg.markerIcon) {
      var pre2 = new Image();
      pre2.src = cfg.markerIconVisited;
    }

    // === When initial images ready (1989 + border) ===
    Promise.all(imagesToLoad).then(function() {
      log('Initial images loaded');
      reportPerf('Phase 1 — first paint');

      // Render markers
      renderMarkers(cfg, markersEl, visited);

      // Set initial layer state (todayReady depends on eager vs lazy load)
      updateLayers(cfg, visited, layerToday, layerBorder, state.todayReady);
      updateProgress(cfg, visited, progressEl);

      // Compute and cache image aspect ratio, then size the canvas
      if (layer1989.naturalWidth && layer1989.naturalHeight) {
        imgRatio = layer1989.naturalWidth / layer1989.naturalHeight;
      }
      canvasDims = resizeCanvas(viewportEl, canvasEl, imgRatio);

      // Clamp current overview position (scale 1) with actual canvas dims
      clampPan();
      applyTransform(false);

      // Hide loader
      if (loaderEl) {
        loaderEl.classList.add('landing-map-loader--hidden');
        setTimeout(function() {
          loaderEl.style.display = 'none';
        }, 600);
      }

      // === Intro zoom: overview → config framing ===
      // Hide markers during intro animation (counter-scale would be wrong)
      markersEl.style.opacity = '0';
      markersEl.style.transition = 'opacity 0.5s ease';

      setTimeout(function() {
        computeInitialFraming();
        // Enable GPU layer for intro animation
        viewportEl.classList.add('landing-map__viewport--animating');
        viewportEl.classList.remove('landing-map__viewport--gesturing');
        viewportEl.style.transition = 'transform 1.8s ease-in-out';
        // Force reflow so browser commits transition before value change
        void viewportEl.offsetHeight;
        // Animate to target
        viewportEl.style.setProperty('--vp-scale', zoom.scale);
        viewportEl.style.setProperty('--vp-tx', zoom.panX + 'px');
        viewportEl.style.setProperty('--vp-ty', zoom.panY + 'px');
        // After intro: remove GPU layer + show markers
        setTimeout(function() {
          viewportEl.style.transition = '';
          viewportEl.classList.remove('landing-map__viewport--animating');
          markersEl.style.opacity = '1';
        }, 1900);
      }, 400);

      // === Phase 2: High-Res im Hintergrund nachladen ===
      function upgradeAllHighRes() {
        upgradeToHighRes(cfg, layer1989, state);
        upgradeTodayHighRes(cfg, layerToday, state);
      }
      if (window.requestIdleCallback) {
        window.requestIdleCallback(upgradeAllHighRes, { timeout: 800 });
      } else {
        setTimeout(upgradeAllHighRes, 800);
      }

      // === Lazy load: border APNG + mapToday ===
      // Trigger 1: requestIdleCallback or setTimeout fallback
      function lazyLoadDeferred() {
        lazyLoadBorder(cfg, layerBorder, visited, state);
        lazyLoadToday(cfg, layerToday, visited, layerBorder, state);
      }

      if (window.requestIdleCallback) {
        window.requestIdleCallback(lazyLoadDeferred, { timeout: 1500 });
      } else {
        setTimeout(lazyLoadDeferred, 1500);
      }

      // Trigger 2: Immediate load on first user interaction
      var interactionEvents = ['pointerdown', 'touchstart', 'keydown'];
      function onFirstInteraction() {
        lazyLoadDeferred();
        interactionEvents.forEach(function(evt) {
          document.removeEventListener(evt, onFirstInteraction);
        });
      }
      interactionEvents.forEach(function(evt) {
        document.addEventListener(evt, onFirstInteraction, { once: false, passive: true });
      });

    }).catch(function(err) {
      console.error('[Landing] Image loading failed:', err);
      if (loaderEl) {
        var text = loaderEl.querySelector('.landing-map-loader__text');
        if (text) text.textContent = 'Karte konnte nicht geladen werden.';
        var spinner = loaderEl.querySelector('.landing-map-loader__spinner');
        if (spinner) spinner.style.display = 'none';
      }
    });

    // === ResizeObserver for responsive canvas repositioning ===
    function onViewportResize() {
      canvasDims = resizeCanvas(viewportEl, canvasEl, imgRatio);
      clampPan();
      applyTransform(false);
    }

    if (window.ResizeObserver) {
      var ro = new ResizeObserver(onViewportResize);
      ro.observe(viewportEl);
    } else {
      var resizeTimer;
      window.addEventListener('resize', function() {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(onViewportResize, 100);
      });
    }

    // =========================================
    // GPU LAYER CLEANUP
    // After animated zoom finishes, remove will-change so the browser
    // re-rasterizes at the new scale — keeps everything crisp.
    // =========================================
    viewportEl.addEventListener('transitionend', function(e) {
      if (e.propertyName === 'transform') {
        // Only remove GPU layer if no newer animation started
        animationCount--;
        if (animationCount <= 0) {
          animationCount = 0;
          viewportEl.classList.remove('landing-map__viewport--animating');
        }
      }
    });

    // =========================================
    // ZOOM / PAN EVENT HANDLERS
    // =========================================

    // Prevent context menu on long press (mobile)
    sectionEl.addEventListener('contextmenu', function(e) { e.preventDefault(); });

    // --- Pointer drag + pinch ---
    var pointers = {};
    var dragStart = null;
    var pinchStartDist = 0;
    var pinchStartScale = 1;

    function getPointerCount() {
      return Object.keys(pointers).length;
    }

    function getPinchDist() {
      var keys = Object.keys(pointers);
      if (keys.length < 2) return 0;
      var a = pointers[keys[0]];
      var b = pointers[keys[1]];
      var dx = a.x - b.x;
      var dy = a.y - b.y;
      return Math.sqrt(dx * dx + dy * dy);
    }

    function getPinchMid() {
      var keys = Object.keys(pointers);
      var a = pointers[keys[0]];
      var b = pointers[keys[1]];
      return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
    }

    // Mobile: no drag/pinch — let touch events scroll the page.
    // Desktop (mouse): drag + pan as before.
    var isTouchDevice = window.matchMedia('(max-width: 1024px)').matches;

    sectionEl.addEventListener('pointerdown', function(e) {
      if (e.button && e.button !== 0) return;
      // Skip touch interactions on mobile — only allow mouse
      if (e.pointerType === 'touch') return;
      pointers[e.pointerId] = { x: e.clientX, y: e.clientY };

      if (getPointerCount() === 1) {
        dragStart = { x: e.clientX, y: e.clientY, panX: zoom.panX, panY: zoom.panY };
        wasDragging = false;
      }

      viewportEl.classList.add('landing-map__viewport--gesturing');
    });

    document.addEventListener('pointermove', function(e) {
      if (!pointers[e.pointerId]) return;
      pointers[e.pointerId] = { x: e.clientX, y: e.clientY };

      if (getPointerCount() === 1 && dragStart) {
        var dx = e.clientX - dragStart.x;
        var dy = e.clientY - dragStart.y;

        if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
          wasDragging = true;
        }

        zoom.panX = dragStart.panX + dx;
        zoom.panY = dragStart.panY + dy;
        clampPan();
        applyTransform(false);
      }
    });

    function onPointerEnd(e) {
      if (!pointers[e.pointerId]) return;
      delete pointers[e.pointerId];

      if (getPointerCount() === 0) {
        dragStart = null;
        viewportEl.classList.remove('landing-map__viewport--gesturing');
        // Reset drag flag after tick so click handler can check it
        setTimeout(function() { wasDragging = false; }, 0);
      }
    }

    document.addEventListener('pointerup', onPointerEnd);
    document.addEventListener('pointercancel', onPointerEnd);

    // --- Touch: Zwei-Finger-Pinch-Zoom + -Pan ---
    // Ein Finger gehört weiter der Seite (touch-action: pan-y → Scroll),
    // erst zwei Finger gehören der Karte. preventDefault auf dem zweiten
    // touchstart verhindert, dass der Browser die Geste als Scroll/Zoom
    // beansprucht — Pointer-Events laufen dann ununterbrochen bei uns auf.
    // State läuft über zoom.* → +/−-Buttons bleiben nach Pinch synchron.
    var touchPts = {};
    var pinch = null;

    function touchCount() {
      return Object.keys(touchPts).length;
    }

    function touchMidDist() {
      var keys = Object.keys(touchPts);
      var a = touchPts[keys[0]];
      var b = touchPts[keys[1]];
      return {
        midX: (a.x + b.x) / 2,
        midY: (a.y + b.y) / 2,
        dist: Math.hypot(a.x - b.x, a.y - b.y)
      };
    }

    sectionEl.addEventListener('touchstart', function(e) {
      if (e.touches.length >= 2) e.preventDefault();
    }, { passive: false });

    sectionEl.addEventListener('touchmove', function(e) {
      if (pinch) e.preventDefault();
    }, { passive: false });

    sectionEl.addEventListener('pointerdown', function(e) {
      if (e.pointerType !== 'touch') return;
      touchPts[e.pointerId] = { x: e.clientX, y: e.clientY };

      if (touchCount() === 2) {
        var rect = sectionEl.getBoundingClientRect();
        var m = touchMidDist();
        pinch = {
          dist: m.dist,
          scale: zoom.scale,
          panX: zoom.panX,
          panY: zoom.panY,
          // Start-Mittelpunkt in Section-Koordinaten
          midX: m.midX - rect.left,
          midY: m.midY - rect.top
        };
        viewportEl.classList.add('landing-map__viewport--gesturing');
        log('Pinch start:', { scale: zoom.scale });
      }
    });

    document.addEventListener('pointermove', function(e) {
      if (!touchPts[e.pointerId]) return;
      touchPts[e.pointerId] = { x: e.clientX, y: e.clientY };
      if (!pinch || touchCount() < 2) return;

      var rect = sectionEl.getBoundingClientRect();
      var m = touchMidDist();
      if (!pinch.dist) return;

      // Stufenloser Zoom, geclampt auf die Button-Grenzen
      var newScale = clamp(pinch.scale * (m.dist / pinch.dist), minZoom, maxZoom);
      var factor = newScale / pinch.scale;

      // Der Weltpunkt unter dem Start-Mittelpunkt folgt dem aktuellen
      // Mittelpunkt → Pinch-Zoom und Zwei-Finger-Pan in einer Rechnung
      var midX = m.midX - rect.left;
      var midY = m.midY - rect.top;
      zoom.scale = newScale;
      zoom.panX = midX - (pinch.midX - pinch.panX) * factor;
      zoom.panY = midY - (pinch.midY - pinch.panY) * factor;

      clampPan();
      applyTransform(false);
    });

    function onTouchEnd(e) {
      if (!touchPts[e.pointerId]) return;
      delete touchPts[e.pointerId];
      if (touchCount() < 2 && pinch) {
        pinch = null;
        viewportEl.classList.remove('landing-map__viewport--gesturing');
        log('Pinch end:', { scale: zoom.scale });
      }
    }

    document.addEventListener('pointerup', onTouchEnd);
    document.addEventListener('pointercancel', onTouchEnd);

    // --- Zoom step helpers ---
    function nextZoomStep(direction) {
      // direction: +1 = zoom in, -1 = zoom out
      var current = zoom.scale;
      var i;
      if (direction > 0) {
        // Find first step strictly greater than current
        for (i = 0; i < zoomSteps.length; i++) {
          if (zoomSteps[i] > current + 0.01) return zoomSteps[i];
        }
        return zoomSteps[zoomSteps.length - 1];
      } else {
        // Find last step strictly less than current
        for (i = zoomSteps.length - 1; i >= 0; i--) {
          if (zoomSteps[i] < current - 0.01) return zoomSteps[i];
        }
        return zoomSteps[0];
      }
    }

    // --- Zoom buttons ---
    var zoomInBtn = document.getElementById('zoomIn');
    var zoomOutBtn = document.getElementById('zoomOut');

    if (zoomInBtn) {
      zoomInBtn.addEventListener('click', function(e) {
        e.stopPropagation();
        zoomCentered(nextZoomStep(+1));
        applyTransform(true);
      });
    }

    if (zoomOutBtn) {
      zoomOutBtn.addEventListener('click', function(e) {
        e.stopPropagation();
        zoomCentered(nextZoomStep(-1));
        applyTransform(true);
      });
    }

    // --- Double-click/tap zoom ---
    sectionEl.addEventListener('dblclick', function(e) {
      if (e.target.closest('.landing-map__marker')) return;
      e.preventDefault();
      if (zoom.scale >= maxZoom - 0.5) {
        // At or near max — zoom back to first step
        zoomAtPoint(zoomSteps[0], e.clientX, e.clientY);
      } else {
        zoomAtPoint(nextZoomStep(+1), e.clientX, e.clientY);
      }
      applyTransform(true);
    });

    // --- Time slider (manual fade) ---
    var timeSlider = document.getElementById('timeSlider');
    var timeSliderContainer = document.getElementById('landingTimeSlider');
    if (timeSlider) {
      // Set initial value to current visited progress
      var initProgress = cfg.markers.length > 0 ? visited.length / cfg.markers.length : 0;
      timeSlider.value = Math.round(initProgress * 100);

      timeSlider.addEventListener('input', function() {
        var sliderProgress = parseInt(this.value, 10) / 100;
        if (state.todayReady) {
          layerToday.style.opacity = sliderProgress;
        }
        if (layerBorder && layerBorder.style.visibility !== 'hidden') {
          layerBorder.style.opacity = 1 - sliderProgress;
        }
      });

      // Prevent slider drag from triggering map pan
      timeSliderContainer.addEventListener('pointerdown', function(e) {
        e.stopPropagation();
      });
    }

    // === DEBUG: Marker placement mode (?debug=markers) ===
    var DEBUG_MARKERS = window.location.search.indexOf('debug=markers') !== -1;
    if (DEBUG_MARKERS) {
      var debugOverlay = document.createElement('div');
      debugOverlay.style.cssText = 'position:fixed;top:1rem;left:1rem;z-index:9999;background:rgba(0,0,0,0.92);color:#0f0;font:14px monospace;padding:12px 16px;border-radius:8px;user-select:text;max-width:400px;';
      debugOverlay.innerHTML = '<b style="color:#fff">Marker Debug</b><br>Klick auf Karte = Koordinaten<br><span id="debugCoords" style="font-size:18px">—</span>';
      document.body.appendChild(debugOverlay);

      // Click on the entire map section — capture phase to beat marker handler
      document.getElementById('landingMap').addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();

        // markersEl IS the image content area — getBoundingClientRect includes CSS transform
        var layerRect = markersEl.getBoundingClientRect();
        var mx = ((e.clientX - layerRect.left) / layerRect.width * 100).toFixed(1);
        var my = ((e.clientY - layerRect.top) / layerRect.height * 100).toFixed(1);

        console.log('[Marker Debug]', { mx: mx, my: my, layerW: Math.round(layerRect.width), layerH: Math.round(layerRect.height) });
        document.getElementById('debugCoords').innerHTML = 'x: <b style="color:#fff">' + mx + '</b>, y: <b style="color:#fff">' + my + '</b>';
      }, true);

      log('Marker debug mode active — clicks show coordinates');
    }

    // === Marker click handler (event delegation) ===
    markersEl.addEventListener('click', function(e) {
      var btn = e.target.closest('.landing-map__marker');
      if (!btn) return;
      if (wasDragging) return;

      var markerId = btn.dataset.markerId;
      var url = btn.dataset.markerUrl;
      if (!markerId || !url) return;

      // Mark as visited
      if (visited.indexOf(markerId) === -1) {
        visited.push(markerId);
        saveVisited(cfg, visited);
        log('Marked visited:', markerId);
      }

      // Visual update
      btn.classList.add('landing-map__marker--visited');
      updateMarkerIcon(btn, cfg.markerIconVisited);
      updateLayers(cfg, visited, layerToday, layerBorder, state.todayReady);
      updateProgress(cfg, visited, progressEl);

      // Sync time slider
      if (timeSlider) {
        timeSlider.value = Math.round((visited.length / cfg.markers.length) * 100);
      }

      // Navigate after short delay (let user see the state change)
      setTimeout(function() {
        window.location.href = url;
      }, 300);
    });

    // === Reset Map button ===
    var resetBtn = document.getElementById('landingResetBtn');
    if (resetBtn) {
      resetBtn.addEventListener('click', function(e) {
        e.stopPropagation();
        // Clear localStorage
        localStorage.removeItem(storageKey(cfg));
        // Reset visited array in-place (closures reference it)
        visited.length = 0;
        // Re-render markers (removes --visited class, swaps icons back)
        renderMarkers(cfg, markersEl, visited);
        // Reset layer opacities
        updateLayers(cfg, visited, layerToday, layerBorder, state.todayReady);
        // Update progress badge
        updateProgress(cfg, visited, progressEl);
        // Reset time slider
        if (timeSlider) timeSlider.value = 0;
        log('Map reset — visited cleared');
      });
    }
  }

  // =========================================
  // BOOT
  // =========================================

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})(window);
